/**
 * Kunden-Detail (H4: Vollseite statt Drawer, Design-Vorlage 3e) — Breadcrumb, Kontaktkarte,
 * Lifetime-KPIs, gemischte Historie (Anfragen/Angebote/Events/Zahlungen, filterbar) und
 * Notizen mit Autor/Datum + Eingabe. Alle Zahlen/Interaktionen aus echten API-Daten.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { useApi, apiMutate } from "../hooks/useApi";
import { apiFetch } from "../lib/api";
import type { Customer, EventRow, GooglePlacesApplicableField, GooglePlacesLookup, HandelsregisterLookup, Inquiry, Payment } from "../ui/types";
import { PAYMENT_STATUS, PAYMENT_TYPE_LABEL, PILL_CLASSES, eventStatusMeta, formatDate, inquirySourceMeta, money } from "../ui/format";
import { EmptyState, ErrorNote, Icon, KpiCard, LoadingRows } from "../ui/components";

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const out = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return out || "?";
}

type HistoryCategory = "anfrage" | "angebot" | "event" | "zahlung";
interface HistoryEntry {
  id: string;
  at: string;
  category: HistoryCategory;
  icon: string;
  title: string;
  detail: string;
  pill?: { label: string; cls: string };
  href?: string;
}

const HISTORY_FILTERS: { key: "all" | HistoryCategory; label: string }[] = [
  { key: "all", label: "Alles" },
  { key: "anfrage", label: "Anfragen" },
  { key: "angebot", label: "Angebote" },
  { key: "event", label: "Events" },
  { key: "zahlung", label: "Zahlungen" },
];

/** H6: Notizen mit Autor/Datum. Da das Datenmodell nur EIN Freitextfeld (internalNotes) kennt,
 *  werden neue Eintraege mit "[Datum · Autor] Text" prefixt und beim Anzeigen geparst. Aeltere,
 *  unprefixte Bestandsnotizen bleiben als ein Eintrag ohne Autor/Datum sichtbar. */
interface NoteEntry {
  author: string;
  date: string;
  text: string;
}
const NOTE_RE = /^\[(.+?) · (.+?)\]\s*([\s\S]*)$/;
function parseNotes(raw: string | null): NoteEntry[] {
  if (!raw?.trim()) return [];
  return raw
    .split(/\n{2,}(?=\[)/)
    .map((block) => {
      const m = NOTE_RE.exec(block.trim());
      return m ? { date: m[1]!, author: m[2]!, text: m[3]!.trim() } : { date: "", author: "", text: block.trim() };
    })
    .filter((n) => n.text);
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useUser();
  const navigate = useNavigate();
  const customerQ = useApi<Customer>(id ? `/api/customers/${id}` : null);
  const eventsQ = useApi<EventRow[]>("/api/events");
  const inquiriesQ = useApi<Inquiry[]>("/api/inquiries");
  const customer = customerQ.data;
  // Datenanreicherung Welle B/C: Mandanten-Schalter + gecachtes Lookup-Ergebnis (null ohne
  // Firma/ohne abgeschlossenen Hintergrund-Job bzw. ohne manuellen Google-Places-Abruf).
  const settingsQ = useApi<{ handelsregisterEnabled?: boolean; googlePlacesEnabled?: boolean } | null>("/api/settings/payment-terms");
  const handelsregisterEnabled = settingsQ.data?.handelsregisterEnabled ?? false;
  const googlePlacesEnabled = settingsQ.data?.googlePlacesEnabled ?? false;
  const hrQ = useApi<HandelsregisterLookup | null>(id ? `/api/customers/${id}/handelsregister` : null);
  const placesQ = useApi<GooglePlacesLookup | null>(id ? `/api/customers/${id}/places-lookup` : null);
  const events = useMemo(() => (eventsQ.data ?? []).filter((e) => e.customerId === id), [eventsQ.data, id]);
  const eventIds = events.map((e) => e.id).join(",");

  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  useEffect(() => {
    if (events.length === 0) {
      setPayments([]);
      setPaymentsError(null);
      return;
    }
    let alive = true;
    (async () => {
      // A-12: zusaetzlich zu HTTP-Fehlern (failed++) auch GEWORFENE Fehler (Netzabbruch,
      // json()-Parsefehler) abfangen - sonst rejected Promise.all unbehandelt und die
      // Offenen-Posten/KPIs blieben ohne Hinweis leer.
      try {
        let failed = 0;
        const lists = await Promise.all(
          events.map(async (e) => {
            const res = await apiFetch(`/api/events/${e.id}/payments`);
            if (!res.ok) {
              failed++;
              return [] as Payment[];
            }
            const body = (await res.json()) as { data: Payment[] };
            return body.data;
          }),
        );
        if (alive) {
          setPayments(lists.flat());
          // N-5: Teil-Fehler beim Fan-out sichtbar machen (KPIs/Historie sonst still unvollständig).
          setPaymentsError(failed > 0 ? `${failed} von ${events.length} Veranstaltungen konnten nicht geladen werden – Zahlen ggf. unvollständig.` : null);
        }
      } catch (err) {
        if (alive) setPaymentsError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIds]);

  // Anfragen, die noch nicht zu einem Event konvertiert wurden (kein customerId auf Inquiry ->
  // bestmoegliche Zuordnung ueber die E-Mail, wie sie beim Erstkontakt hinterlegt wurde).
  const openInquiries = useMemo(
    () =>
      (inquiriesQ.data ?? []).filter(
        (inq) => !inq.generatedEventId && customer && (inq.customerEmail ?? "").toLowerCase() === customer.email.toLowerCase(),
      ),
    [inquiriesQ.data, customer],
  );

  const [historyFilter, setHistoryFilter] = useState<"all" | HistoryCategory>("all");
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  const lifetimeCents = events.filter((e) => ["paid", "completed"].includes(e.status)).reduce((s, e) => s + (e.amountTotalCents ?? 0), 0);
  const avgOrderCents = events.length > 0 ? Math.round(lifetimeCents / events.length) : 0;
  const inquiryCount = events.filter((e) => e.status === "inquiry").length + openInquiries.length;
  const openBalanceCents = payments.filter((p) => ["pending", "processing"].includes(p.status)).reduce((s, p) => s + p.amountCents, 0);

  const isCompany = Boolean(customer?.company);
  const isRegular = events.filter((e) => ["paid", "completed"].includes(e.status)).length >= 2;
  const customerSinceLabel = customer ? new Date(customer.createdAt).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "";

  const history = useMemo<HistoryEntry[]>(() => {
    const entries: HistoryEntry[] = [];
    for (const inq of openInquiries) {
      entries.push({
        id: `inq-${inq.id}`,
        at: inq.createdAt,
        category: "anfrage",
        icon: "mail",
        title: "Anfrage eingegangen",
        detail: inq.message ? `„${inq.message.slice(0, 140)}${inq.message.length > 140 ? "…" : ""}"` : inquirySourceMeta(inq.source).label,
        // CX-Fix 2026-07-18: Deep-Link in den Drawer dieser Anfrage (?open=).
        href: `/anfragen?open=${inq.id}`,
      });
    }
    for (const e of events) {
      const sm = eventStatusMeta(e.status); // N-11: unbekannter Status crasht die Historie nicht
      entries.push({
        id: `created-${e.id}`,
        at: e.createdAt,
        category: "anfrage",
        icon: "mail",
        title: `Anfrage eingegangen — ${e.occasion ?? e.number ?? "Veranstaltung"}`,
        detail: [e.guestCount ? `${e.guestCount} Gäste` : null, e.location].filter(Boolean).join(" · "),
        // CX-Audit 2026-07-17 (P1): Ohne href waren laufende Vorgänge (inquiry/offer_draft/
        // no_response ohne offerSentAt) vom Kundenprofil aus ÜBERHAUPT nicht erreichbar —
        // der einzige Weg führte über den Umweg /veranstaltungen. Jetzt öffnet der Eintrag
        // den Vorgang direkt.
        href: `/veranstaltungen/${e.id}`,
      });
      if (e.offerSentAt) {
        entries.push({
          id: `offer-${e.id}`,
          at: e.offerSentAt,
          category: "angebot",
          icon: "send",
          title: `Angebot ${e.number ?? ""} gesendet`.trim(),
          detail: [e.occasion, e.guestCount ? `${e.guestCount} Gäste` : null, e.location, money(e.amountTotalCents)].filter(Boolean).join(" · "),
          pill: { label: sm.label, cls: PILL_CLASSES[sm.tone] },
          href: `/veranstaltungen/${e.id}/angebot`,
        });
      }
      if (["completed", "cancelled", "paid"].includes(e.status)) {
        entries.push({
          id: `event-${e.id}`,
          at: e.eventDate ?? e.createdAt,
          category: "event",
          icon: "event",
          title: `${e.occasion ?? "Veranstaltung"} ${e.status === "cancelled" ? "storniert" : "durchgeführt"}`,
          detail: [e.guestCount ? `${e.guestCount} Gäste` : null, e.location, money(e.amountTotalCents)].filter(Boolean).join(" · "),
          pill: { label: sm.label, cls: PILL_CLASSES[sm.tone] },
          href: `/veranstaltungen/${e.id}`,
        });
      }
    }
    for (const p of payments) {
      if (!["succeeded", "refunded"].includes(p.status)) continue;
      const meta = PAYMENT_STATUS[p.status];
      entries.push({
        id: `pay-${p.id}`,
        at: p.paidAt ?? p.createdAt,
        category: "zahlung",
        icon: "payments",
        title: p.status === "refunded" ? "Erstattung gebucht" : "Zahlung eingegangen",
        detail: `${money(p.amountCents)} · ${PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType}`,
        pill: meta ? { label: meta.label, cls: PILL_CLASSES[meta.tone] } : undefined,
        // CX-Audit 2026-07-17: Zahlung trägt ihre eventId — Klick öffnet den zugehörigen
        // Vorgang mit der Abrechnungskarte (vorher toter Eintrag).
        href: `/veranstaltungen/${p.eventId}`,
      });
    }
    return entries.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [events, openInquiries, payments]);

  const filteredHistory = historyFilter === "all" ? history : history.filter((h) => h.category === historyFilter);

  async function addNote() {
    if (!id || !customer || !noteDraft.trim()) return;
    setNoteBusy(true);
    setNoteError(null);
    try {
      const entry = `[${formatDate(new Date().toISOString())} · ${user?.displayName ?? "Team"}] ${noteDraft.trim()}`;
      const updated = customer.internalNotes ? `${entry}\n\n${customer.internalNotes}` : entry;
      // M-46: internalNotesBase = der Stand, auf dem dieser Eintrag aufsetzt - der Server lehnt
      // mit 409 ab, wenn jemand anderes zwischenzeitlich gespeichert hat (kein stiller Verlust).
      await apiMutate(`/api/customers/${id}`, "PATCH", { internalNotes: updated, internalNotesBase: customer.internalNotes ?? null });
      setNoteDraft("");
      customerQ.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (/notes_conflict|zwischenzeitlich/i.test(msg)) {
        setNoteError("Die Notizen wurden gerade von jemand anderem ergänzt – Liste wurde neu geladen, dein Text steht noch im Eingabefeld. Bitte erneut auf Hinzufügen klicken.");
        customerQ.reload(); // frischen Stand holen; noteDraft bleibt erhalten
      } else {
        setNoteError(msg);
      }
    } finally {
      setNoteBusy(false);
    }
  }

  if (customerQ.loading) return <LoadingRows rows={6} />;
  if (customerQ.error) return <ErrorNote message={customerQ.error} />;
  if (!customer) return <EmptyState icon="person_off" title="Kunde nicht gefunden" />;

  const notes = parseNotes(customer.internalNotes);
  const address = [customer.addressStreet, `${customer.addressZip ?? ""} ${customer.addressCity ?? ""}`.trim()].filter(Boolean).join(", ");

  return (
    <>
      {/* H4: Breadcrumb statt Drawer-Titel */}
      <div className="mb-4 flex items-center gap-2 font-body-sm text-body-sm">
        <Link to="/kunden" className="text-secondary hover:text-primary dark:text-secondary-fixed-dim">
          Kunden
        </Link>
        <Icon name="chevron_right" className="text-[14px] text-secondary-fixed-dim" />
        <span className="font-semibold text-on-background dark:text-inverse-on-surface">{customer.name}</span>
      </div>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-primary/14 text-[17px] font-bold text-primary">
            {initials(customer.name)}
          </div>
          <div>
            {/* H7: Badges */}
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-[25px] font-bold leading-tight tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">{customer.name}</h1>
              {isCompany && (
                <span className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${PILL_CLASSES.neutral}`}>Firmenkunde</span>
              )}
              {isRegular && (
                <span className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${PILL_CLASSES.success}`}>Stammkunde</span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-secondary dark:text-secondary-fixed-dim">
              {[customer.company, `Kundin/Kunde seit ${customerSinceLabel}`].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <a href={`mailto:${customer.email}`} className="btn-secondary">
            <Icon name="mail" className="text-[16px]" /> Kunde anschreiben
          </a>
          <button type="button" className="btn-primary" onClick={() => navigate(`/veranstaltungen?newFor=${customer.id}`)}>
            <Icon name="add" className="text-[16px]" /> Angebot erstellen
          </button>
        </div>
      </div>

      {/* CX-Audit 2026-07-17: KPI-Kacheln beantworten ihre Frage per Klick — „Veranstaltungen"
          filtert die Historie unten, „Offene Posten" springt in die Zahlungsübersicht mit
          vorbefüllter Kundensuche. */}
      <div className="mb-5 grid grid-cols-2 gap-3.5 md:grid-cols-4">
        <KpiCard label="Umsatz gesamt" value={money(lifetimeCents)} icon="payments" />
        <button
          type="button"
          className="text-left"
          title="Historie nach Veranstaltungen filtern"
          onClick={() => {
            setHistoryFilter("event");
            document.getElementById("kunden-historie")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          <KpiCard
            label="Veranstaltungen"
            value={events.length}
            icon="event"
            delta={inquiryCount > 0 ? { text: `+${inquiryCount} angefragt`, tone: "warn" } : undefined}
          />
        </button>
        <KpiCard label="Ø Auftragswert" value={money(avgOrderCents)} icon="calculate" />
        <KpiCard
          label="Offene Posten"
          value={money(openBalanceCents)}
          icon="account_balance_wallet"
          to={`/zahlungen?filter=open&q=${encodeURIComponent(customer.name)}`}
          delta={openBalanceCents > 0 ? { text: "offen", tone: "warn" } : undefined}
        />
      </div>

      {paymentsError && <div className="mb-5"><ErrorNote message={paymentsError} /></div>}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_1.5fr]">
        <div className="flex flex-col gap-4">
          {/* Kontakt */}
          <div className="card">
            <div className="border-b border-outline-variant/30 px-[18px] py-[14px] dark:border-outline/30">
              <h3 className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Kontakt</h3>
            </div>
            <div className="flex flex-col gap-3 px-[18px] py-[14px]">
              <a
                href={`mailto:${customer.email}`}
                className="flex items-center gap-2.5 text-[12.5px] font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface"
              >
                <Icon name="mail" className="text-[16px] text-secondary" /> <span className="truncate">{customer.email}</span>
              </a>
              {customer.phone && (
                <div className="flex items-center gap-2.5 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  <Icon name="call" className="text-[16px] text-secondary" /> {customer.phone}
                </div>
              )}
              {address && (
                <div className="flex items-center gap-2.5 text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                  <Icon name="location_on" className="text-[16px] text-secondary" /> {address}
                </div>
              )}
            </div>
          </div>

          {/* Handelsregister + Bundesanzeiger + KI-Kurzzusammenfassung (Datenanreicherung
              Welle B) — nur bei gesetzter Firma UND aktivem Mandanten-Schalter. */}
          {customer.company && handelsregisterEnabled && (
            <HandelsregisterCard company={customer.company} lookup={hrQ.data} loading={hrQ.loading} onReload={hrQ.reload} />
          )}

          {/* Google Places (Datenanreicherung Welle C) — manueller Lookup-Button, gated auf den
              Mandanten-Schalter (die Route selbst gated zusaetzlich auf GOOGLE_PLACES_API_KEY,
              siehe settings.tsx SystemTab-Health-Flag "places"). */}
          {googlePlacesEnabled && (
            <GooglePlacesCard
              customerId={customer.id}
              company={customer.company}
              addressCity={customer.addressCity}
              lookup={placesQ.data}
              loading={placesQ.loading}
              onReload={placesQ.reload}
              onCustomerUpdated={customerQ.reload}
            />
          )}

          {/* H6: Notizen mit Autor/Datum + Eingabe */}
          <div className="card">
            <div className="border-b border-outline-variant/30 px-[18px] py-[14px] dark:border-outline/30">
              <h3 className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Notizen</h3>
            </div>
            <div className="flex flex-col gap-3 px-[18px] py-[14px]">
              {notes.length === 0 ? (
                <p className="text-[12.5px] text-secondary-fixed-dim">Noch keine Notizen.</p>
              ) : (
                notes.map((n, idx) => (
                  <div key={idx} className="flex gap-2.5">
                    <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-primary/14 text-[10px] font-bold text-primary">
                      {n.author ? initials(n.author) : "?"}
                    </span>
                    <div>
                      <p className="text-[12.5px] leading-[1.55] text-on-background dark:text-inverse-on-surface">{n.text}</p>
                      {(n.author || n.date) && (
                        <p className="mt-0.5 text-[10.5px] text-secondary-fixed-dim">{[n.author, n.date].filter(Boolean).join(" · ")}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
              {noteError && <ErrorNote message={noteError} />}
              <div className="flex items-center gap-2">
                <input
                  className="input !py-2 text-[12.5px]"
                  placeholder="Notiz hinzufügen …"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void addNote();
                  }}
                  disabled={noteBusy}
                />
                <button type="button" className="btn-secondary shrink-0 !py-2" disabled={noteBusy || !noteDraft.trim()} onClick={() => void addNote()}>
                  {noteBusy ? "…" : "Hinzufügen"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* H5: gemischte Historie. id: Scroll-Ziel der KPI-Kachel „Veranstaltungen". */}
        <div id="kunden-historie" className="card scroll-mt-20">
          <div className="flex flex-wrap items-center gap-3.5 border-b border-outline-variant/30 px-[18px] py-[14px] dark:border-outline/30">
            <h3 className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Historie</h3>
            <div className="ml-auto flex flex-wrap gap-1.5">
              {HISTORY_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setHistoryFilter(f.key)}
                  className={`rounded-full px-2.5 py-1 font-label-md text-[11px] font-semibold ${
                    historyFilter === f.key
                      ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
                      : "border border-outline-variant/50 text-on-surface-variant dark:border-outline/40 dark:text-secondary-fixed-dim"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="px-[18px] py-4">
            {eventsQ.loading || inquiriesQ.loading ? (
              <LoadingRows rows={4} />
            ) : filteredHistory.length === 0 ? (
              <EmptyState icon="history" title="Noch keine Einträge" hint="Anfragen, Angebote, Events und Zahlungen erscheinen hier." />
            ) : (
              <ul>
                {filteredHistory.map((h, idx) => (
                  <li key={h.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/12 text-primary">
                        <Icon name={h.icon} className="text-[14px]" />
                      </span>
                      {idx < filteredHistory.length - 1 && <span className="w-px flex-1 bg-outline-variant/40 dark:bg-outline/30" />}
                    </div>
                    <div className={idx < filteredHistory.length - 1 ? "flex-1 pb-4" : "flex-1"}>
                      <div className="flex flex-wrap items-center gap-2">
                        {h.href ? (
                          <Link to={h.href} className="text-[13px] font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface">
                            {h.title}
                          </Link>
                        ) : (
                          <span className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{h.title}</span>
                        )}
                        {h.pill && <span className={`rounded-full px-2 py-0.5 font-label-md text-[10.5px] font-semibold ${h.pill.cls}`}>{h.pill.label}</span>}
                        <span className="ml-auto text-[11px] text-secondary-fixed-dim">{formatDate(h.at)}</span>
                      </div>
                      {h.detail && <p className="mt-0.5 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{h.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
/** Basis-Suchseite fuer die manuelle Insolvenz-Pruefung — bewusst OHNE Query-Parameter (kein
 *  Auto-Fill, kein automatisierter Abruf). Der Name muss selbst eingegeben werden, siehe
 *  Welle-B-Plan ("robots.txt verbietet Automatisierung, DSGVO-sensible personenbezogene Daten"). */
const INSOLVENZ_SEARCH_URL = "https://neu.insolvenzbekanntmachungen.de/";

const HR_STATUS_LABEL: Record<string, string> = {
  pending: "Abruf läuft im Hintergrund …",
  not_found: "Keine Handelsregister-Eintragung gefunden.",
  error: "Abruf fehlgeschlagen.",
};

/**
 * Datenanreicherung Welle B: Handelsregister + Bundesanzeiger + KI-Kurzzusammenfassung im
 * Kunden-Drawer. `lookup` ist null, solange der Hintergrund-Job (waitUntil, ausgelöst beim
 * Anlegen/Ändern der Firma) noch nicht abgeschlossen ist ODER noch gar nicht ausgelöst wurde —
 * daher der "Aktualisieren"-Button (kein Auto-Polling, um keine unnötige Request-Last zu erzeugen).
 */
function HandelsregisterCard({
  company,
  lookup,
  loading,
  onReload,
}: {
  company: string;
  lookup: HandelsregisterLookup | null;
  loading: boolean;
  onReload: () => void;
}) {
  const matches = lookup?.matches ?? [];
  const primary = matches[0] ?? null;
  const filings = lookup?.bundesanzeigerFilings?.filings ?? [];
  const searchUrl = lookup?.bundesanzeigerFilings?.searchUrl ?? null;

  // Fuer die manuelle Insolvenz-Pruefung: Firma + jeder gefundene Geschaeftsfuehrer-/Prokura-Name
  // (dedupliziert, ueber alle Treffer hinweg falls mehrdeutig) — reine Verlinkung, siehe unten.
  const insolvencyNames = Array.from(
    new Set([primary?.companyName ?? company, ...matches.flatMap((m) => m.representatives)].filter(Boolean)),
  );

  return (
    <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-surface-variant/10">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
          Handelsregister &amp; Bonität
        </p>
        <button
          type="button"
          onClick={onReload}
          className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
        >
          Aktualisieren
        </button>
      </div>

      {loading ? (
        <LoadingRows rows={2} />
      ) : !lookup ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant/70">
          Noch kein Ergebnis für „{company}" — der Abruf läuft einmalig im Hintergrund (kann einige Sekunden dauern).
        </p>
      ) : lookup.status === "pending" || lookup.status === "not_found" || lookup.status === "error" ? (
        <div>
          <p className="font-body-sm text-body-sm text-on-surface-variant/70">{HR_STATUS_LABEL[lookup.status]}</p>
          {lookup.status === "error" && lookup.errorMessage && (
            <p className="mt-1 font-body-sm text-body-sm text-error">{lookup.errorMessage}</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* KI-Kurzzusammenfassung (Bonitäts-Einordnung) — ganz oben. */}
          {lookup.aiSummary && (
            <p className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3 font-body-sm text-body-sm italic text-on-background dark:border-outline/20 dark:bg-surface-variant/5 dark:text-inverse-on-surface">
              {lookup.aiSummary}
            </p>
          )}

          {lookup.status === "ambiguous" && (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {matches.length} mögliche Treffer im Handelsregister gefunden — nicht eindeutig zuordenbar, bitte manuell prüfen:{" "}
              {matches.map((m) => m.companyName).join(", ")}.
            </p>
          )}

          {/* Rohdaten — nur bei eindeutigem Treffer sinnvoll darstellbar. */}
          {lookup.status === "found" && primary && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-body-sm text-body-sm">
              {primary.legalForm && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Rechtsform</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">{primary.legalForm}</dd>
                </>
              )}
              {(primary.registerCourt || primary.registerNumber) && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Register</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">
                    {[primary.registerCourt, primary.registerNumber].filter(Boolean).join(" · ")}
                  </dd>
                </>
              )}
              {primary.seat && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Sitz</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">{primary.seat}</dd>
                </>
              )}
              {primary.representatives.length > 0 && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Geschäftsführer</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">{primary.representatives.join(", ")}</dd>
                </>
              )}
              {primary.lastEntryDate && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Letzte Eintragung</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">{formatDate(primary.lastEntryDate)}</dd>
                </>
              )}
            </dl>
          )}

          {/* Bundesanzeiger-Veröffentlichungen (Titel + Datum, nie Einzeldokumente abgerufen). */}
          <div>
            <p className="mb-1.5 font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Bundesanzeiger-Veröffentlichungen
            </p>
            {filings.length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant/70">Keine Veröffentlichungen gefunden.</p>
            ) : (
              <ul className="space-y-1">
                {filings.map((f, i) => (
                  <li key={i} className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    {f.title}
                    {f.publishedAt && <span className="text-secondary dark:text-secondary-fixed-dim"> · {formatDate(f.publishedAt)}</span>}
                  </li>
                ))}
              </ul>
            )}
            {searchUrl && (
              <a
                href={searchUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
              >
                Auf Bundesanzeiger ansehen <Icon name="open_in_new" className="text-[15px]" />
              </a>
            )}
          </div>

          {/* Klar abgesetzt: manuelle Insolvenz-Prüfung — reine Verlinkung, KEIN automatisierter
              Abruf/Auto-Fill (siehe Kommentar bei INSOLVENZ_SEARCH_URL). */}
          <div className="rounded-lg border-2 border-dashed border-outline-variant/50 p-3 dark:border-outline/40">
            <p className="font-label-md text-[11px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Insolvenz-Prüfung (manuell)
            </p>
            <p className="mt-1 font-body-sm text-[11px] text-on-surface-variant/70">
              Kein automatischer Abruf möglich (insolvenzbekanntmachungen.de verbietet Automatisierung) — Name bitte im
              geöffneten Portal selbst eingeben.
            </p>
            <div className="mt-2 space-y-1">
              {insolvencyNames.map((name) => (
                <a
                  key={name}
                  href={INSOLVENZ_SEARCH_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 font-body-sm text-body-sm text-primary hover:underline dark:text-primary-fixed-dim"
                >
                  <Icon name="search" className="text-[15px]" /> {name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Anzeigename + Reihenfolge der drei uebernehmbaren Felder. rating/userRatingsTotal/
 *  openingHours/businessStatus sind bewusst NICHT hier gelistet (rein informativ, siehe
 *  ui/types.ts GooglePlacesApplicableField). */
const PLACES_FIELDS: { key: GooglePlacesApplicableField; label: string; value: (l: GooglePlacesLookup) => string | null }[] = [
  { key: "company", label: "Firmenname", value: (l) => l.suggestedName },
  { key: "phone", label: "Telefon", value: (l) => l.suggestedPhone },
  { key: "address", label: "Adresse", value: (l) => l.suggestedFormattedAddress },
];

/**
 * Datenanreicherung Welle C: Google Places (New) auf der Kunden-Vollseite. Im Unterschied zur
 * Handelsregister-Karte (automatischer Hintergrund-Job) ist das hier ein MANUELLER, von einem
 * Menschen ausgeloester Abruf (Button "Nachschlagen") — Suchfeld vorbefuellt mit
 * "${Firma}, ${Ort}", vor dem Abruf frei editierbar. Jedes uebernehmbare Feld (Firmenname/
 * Telefon/Adresse) hat einen EIGENEN "Übernehmen"-Button, der gezielt NUR dieses eine Feld setzt
 * (nie ein Bulk-Overwrite) — nach Erfolg zeigt die Zeile "✓ übernommen" statt des Buttons.
 */
function GooglePlacesCard({
  customerId,
  company,
  addressCity,
  lookup,
  loading,
  onReload,
  onCustomerUpdated,
}: {
  customerId: string;
  company: string | null;
  addressCity: string | null;
  lookup: GooglePlacesLookup | null;
  loading: boolean;
  onReload: () => void;
  onCustomerUpdated: () => void;
}) {
  const defaultQuery = [company, addressCity].filter(Boolean).join(", ");
  const [query, setQuery] = useState(lookup?.query ?? defaultQuery);
  const [busy, setBusy] = useState(false);
  const [applyingField, setApplyingField] = useState<GooglePlacesApplicableField | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Suchfeld beim Kundenwechsel neu vorbefuellen (bestehende query, falls schon abgefragt, sonst
  // der Default aus Firma+Ort).
  useEffect(() => {
    setQuery(lookup?.query ?? defaultQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  async function handleLookup() {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/customers/${customerId}/places-lookup`, "POST", query.trim() ? { query: query.trim() } : {});
      onReload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleApply(field: GooglePlacesApplicableField) {
    if (!lookup?.id || !lookup.lookedUpAt) return;
    setApplyingField(field);
    setError(null);
    try {
      // M-45: (id, lookedUpAt) = Version des angezeigten Ergebnisses - der Server lehnt mit 409
      // ab, wenn ein anderer Tab den Lookup inzwischen ausgetauscht hat (kein stilles Uebernehmen).
      await apiMutate(`/api/customers/${customerId}/places-lookup`, "PATCH", { appliedField: field, lookupId: lookup.id, lookedUpAt: lookup.lookedUpAt });
      onReload();
      onCustomerUpdated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(/lookup_changed|neu abgerufen/i.test(msg)
        ? "Das Ergebnis wurde inzwischen neu abgerufen – bitte den aktuellen Vorschlag prüfen und erneut übernehmen."
        : msg);
      onReload(); // aktuellen Vorschlag anzeigen
    } finally {
      setApplyingField(null);
    }
  }

  return (
    <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-5 dark:border-outline/30 dark:bg-surface-variant/10">
      <p className="mb-3 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        Google Places
      </p>

      <div className="flex items-center gap-2">
        <input
          className="input flex-1"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Firma, Ort …"
        />
        <button
          type="button"
          className="btn-secondary shrink-0 !py-2"
          disabled={busy || !query.trim()}
          onClick={() => void handleLookup()}
        >
          {busy ? "Sucht …" : "Nachschlagen"}
        </button>
      </div>
      {error && <ErrorNote message={error} />}

      {loading ? (
        <div className="mt-3">
          <LoadingRows rows={2} />
        </div>
      ) : !lookup ? (
        <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant/70">
          Noch kein Ergebnis — Suchanfrage oben prüfen und „Nachschlagen" klicken.
        </p>
      ) : !lookup.placeId ? (
        <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant/70">
          Keine Treffer für „{lookup.query}".
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Immer sichtbarer Lese-Block — rein informativ, nie übernehmbar (kein customers-
              Spalten-Gegenstück für Bewertung/Öffnungszeiten/Status). */}
          {(lookup.rating != null || lookup.businessStatus) && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-body-sm text-body-sm">
              {lookup.rating != null && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Bewertung</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">
                    {lookup.rating.toFixed(1)} ★{lookup.userRatingsTotal != null ? ` (${lookup.userRatingsTotal})` : ""}
                  </dd>
                </>
              )}
              {lookup.businessStatus && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Status</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">{lookup.businessStatus}</dd>
                </>
              )}
            </dl>
          )}
          {lookup.openingHours && lookup.openingHours.weekdayDescriptions.length > 0 && (
            <ul className="space-y-0.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              {lookup.openingHours.weekdayDescriptions.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}

          {/* Je Feld: "Übernehmen"-Button ODER "✓ übernommen" — appliedFields ist reiner
              UI-Zustand, kein Verbot, ein Feld erneut zu übernehmen. */}
          <div className="space-y-2">
            {PLACES_FIELDS.map(({ key, label, value }) => {
              const suggested = value(lookup);
              if (!suggested) return null;
              const applied = lookup.appliedFields[key] === true;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/30 p-3 dark:border-outline/20"
                >
                  <div className="min-w-0">
                    <p className="font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                      {label}
                    </p>
                    <p className="truncate font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                      {suggested}
                    </p>
                  </div>
                  {applied ? (
                    <span className="flex shrink-0 items-center gap-1 font-label-md text-label-md text-green-700 dark:text-green-400">
                      <Icon name="check_circle" className="text-[16px]" /> übernommen
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary shrink-0 !px-3 !py-1.5"
                      disabled={applyingField === key}
                      onClick={() => void handleApply(key)}
                    >
                      {applyingField === key ? "…" : "Übernehmen"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
